import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useAdminSettingsList,
  type AdminSetting,
  type SettingSection,
} from "@/queries/adminSettings";

import { SettingRow } from "./SettingRow";

// Stable section order — must match the server's grouping in
// `admin_settings.SECTION_ORDER`. Empty sections are hidden.
const SECTION_ORDER: readonly SettingSection[] = [
  "llm",
  "database",
  "ibkr",
  "data_sources",
  "feature_flags",
] as const;

export function AdminSettingsPage() {
  const { t } = useTranslation("admin-settings");
  const { data, isPending, isError } = useAdminSettingsList();

  const bySection = useMemo(() => groupBySection(data ?? []), [data]);

  if (isPending) {
    return <PageShell><p className="text-sm text-muted-foreground">{t("page.loading")}</p></PageShell>;
  }
  if (isError) {
    return (
      <PageShell>
        <Alert variant="destructive">
          <AlertDescription>{t("page.loadError")}</AlertDescription>
        </Alert>
      </PageShell>
    );
  }
  if (!data || data.length === 0) {
    return <PageShell><p className="text-sm text-muted-foreground">{t("page.empty")}</p></PageShell>;
  }

  return (
    <PageShell>
      <div className="flex flex-col gap-6">
        {SECTION_ORDER.map((section) => {
          const rows = bySection.get(section);
          if (!rows || rows.length === 0) return null;
          return (
            <Card key={section} data-testid={`section-${section}`}>
              <CardHeader>
                <CardTitle>{t(`sections.${section}.title`)}</CardTitle>
                <CardDescription>
                  {t(`sections.${section}.description`)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="flex flex-col">
                  {rows.map((row) => (
                    <SettingRow key={row.key} setting={row} />
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation("admin-settings");
  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">{t("page.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("page.subtitle")}</p>
      </header>
      {children}
    </div>
  );
}

function groupBySection(rows: AdminSetting[]): Map<SettingSection, AdminSetting[]> {
  const out = new Map<SettingSection, AdminSetting[]>();
  for (const row of rows) {
    const bucket = out.get(row.section);
    if (bucket) bucket.push(row);
    else out.set(row.section, [row]);
  }
  return out;
}
